import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { VISO_PLATFORMS } from '@/data/vplatforms';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowUpRight, Camera, CheckCircle2, ScanLine } from 'lucide-react';

function ProductBlock({ product, accent }) {
  return (
    <section id={product.id} className="scroll-mt-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">
            {product.num} · {product.name} · <span className="text-slate-400">{product.kicker}</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">
            {product.headline}
          </h2>
          <p className="text-slate-400 mt-4 leading-relaxed">{product.body}</p>
          {product.bullets && (
            <ul className="mt-6 space-y-2">
              {product.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {product.footer && (
            <p className="text-xs text-slate-500 mt-6 leading-relaxed border-l-2 border-cyan-400/30 pl-3">
              {product.footer}
            </p>
          )}
        </div>

        <div className="md:col-span-7">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-6 viso-grid relative overflow-hidden">
            {/* Demo panel */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ScanLine className="w-3.5 h-3.5 text-cyan-300" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  Live demo · {product.name}
                </div>
              </div>
              <div className="text-[10px] font-mono text-cyan-300 flex items-center gap-1.5">
                <span className="dot-pulse" />running
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(product.bullets || []).slice(0, 4).map((b, k) => (
                <div
                  key={k}
                  className="rounded-md bg-black/40 border border-white/5 px-3 py-2.5 flex items-center gap-2"
                >
                  <Camera className="w-3 h-3 text-cyan-300 flex-shrink-0" />
                  <span className="text-[11px] text-slate-300 truncate">{b}</span>
                </div>
              ))}
            </div>

            {product.stats && product.stats.length > 0 && (
              <div className="mt-5 grid grid-cols-2 gap-3">
                {product.stats.map((s, i) => (
                  <div key={i} className="rounded-md bg-black/50 border border-cyan-400/20 px-3 py-3">
                    <div className={`font-display text-2xl font-bold bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
                      {s.v}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function VisoPlatformPage() {
  const { slug } = useParams();
  const location = useLocation();
  const platform = VISO_PLATFORMS[slug];

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.hash, slug]);

  if (!platform) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="text-cyan-300 text-xs font-mono uppercase tracking-widest">404</div>
        <h1 className="font-display text-3xl font-bold mt-3">Platform not found</h1>
        <Link to="/platform" className="text-cyan-300 hover:text-cyan-200 text-sm mt-4 inline-block">
          &larr; Back to Platform
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={platform.poster} alt={platform.name} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07090c]/70 via-[#07090c]/85 to-[#07090c]" />
          <div className="absolute inset-0 viso-grid opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300 mb-4">
            {platform.kicker}
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] max-w-4xl">
            <span className={`bg-gradient-to-r ${platform.accent} bg-clip-text text-transparent`}>
              {platform.name}
            </span>
          </h1>
          <div className="text-slate-300 mt-3 text-lg md:text-xl">{platform.tagline}</div>
          <p className="text-slate-400 mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            {platform.hero}
          </p>

          {/* Anchor nav */}
          <div className="mt-10 flex flex-wrap gap-2">
            {platform.products.map((p) => (
              <a
                key={p.id}
                href={`#${p.id}`}
                className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.08] transition-colors"
              >
                <span className="text-[10px] font-mono text-slate-500">{p.num}</span>
                <span className="text-xs text-slate-200 group-hover:text-cyan-300 transition-colors">
                  {p.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Product blocks */}
      {platform.products.map((p) => (
        <ProductBlock key={p.id} product={p} accent={platform.accent} />
      ))}

      {/* CTA */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 md:p-16">
            <div className="absolute inset-0 viso-grid opacity-60" />
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-400/15 blur-3xl" />
            <div className="relative">
              <h3 className="font-display text-3xl md:text-5xl font-bold max-w-3xl">
                {platform.cta.title}
              </h3>
              <p className="text-slate-400 mt-4 max-w-2xl">{platform.cta.sub}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                    Talk to us <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/platform">
                  <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">
                    Explore the platform <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
